import { Loader2 } from "lucide-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { homePathForUser, useAuthStore } from "@/client/features/auth/auth-store";
import type { UserKind } from "@/shared/types";

/**
 * Guards an authenticated area. Waits for the initial session check, redirects
 * anonymous visitors to /login (remembering where they were headed), and sends
 * a signed-in principal to their own area if they hit the wrong audience.
 */
export function AuthGate({ audience }: { audience: UserKind }) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-svh items-center justify-center" aria-busy="true">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (status === "anonymous" || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (user.kind !== audience) {
    return <Navigate to={homePathForUser(user)} replace />;
  }

  return <Outlet />;
}
